import { useEffect, useState } from "react";
import { supabase } from "../lib/supabaseclient";
import { useCart } from "../shared/CartContext";
import { motion, AnimatePresence } from "framer-motion";
import { Server, Building2, Monitor, ChevronDown, ChevronUp } from "lucide-react";

export default function Hosting() {
  const { addItem } = useCart();
  const [allPlans, setAllPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState("shared");
  const [addingKey, setAddingKey] = useState(null);
  const [addedKey, setAddedKey] = useState(null);
  const [toast, setToast] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    async function fetchPlans() {
      const { data, error } = await supabase
        .from("hosting_plans")
        .select("*")
        .order("price", { ascending: true });

      if (error) console.error("Error fetching plans:", error);
      else setAllPlans(data || []);

      setLoading(false);
    }
    fetchPlans();
  }, []);

  // Detect screen width for mobile / tablet view
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 1024); // <1024px = mobile/tablet
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const filteredPlans = allPlans
    .filter((p) => p.category === category)
    .sort((a, b) => a.price - b.price);

  const categories = [
    { key: "shared", label: "Shared Hosting", icon: <Server className="w-4 h-4 mr-2" /> },
    { key: "business", label: "Business Hosting", icon: <Building2 className="w-4 h-4 mr-2" /> },
    { key: "windows", label: "Windows Hosting", icon: <Monitor className="w-4 h-4 mr-2" /> },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-pulse text-gray-600 text-lg">Loading plans...</div>
      </div>
    );
  }

  return (
    <div className="text-gray-800">
      {/* Hero Section */}
      <section className="relative bg-[linear-gradient(160deg,#1e40af_0%,#0891b2_100%)] text-white py-20">
        <div className="container mx-auto px-6 text-center">
          <h1 className="text-4xl sm:text-5xl font-extrabold">Hosting Plans</h1>
          <p className="mt-4 text-blue-100 max-w-2xl mx-auto text-lg">
            Choose a plan that fits your project. Fast, reliable hosting with 99.9% uptime guarantee.
          </p>
        </div>
      </section>


      {/* Main Content */}
      <section className="container mx-auto px-6 py-16">
        {/* Category Selector */}
        <div className="flex flex-wrap gap-3 mb-8 justify-center">
          {categories.map((cat) => (
            <button
              key={cat.key}
              onClick={() => setCategory(cat.key)}
              className={`flex items-center px-5 py-2 rounded-full border transition-all duration-300 ${category === cat.key
                  ? "bg-blue-600 text-white border-blue-600 shadow-md"
                  : "bg-white text-gray-700 border-gray-300 hover:bg-gray-100"
                }`}
            >
              {cat.icon}
              {cat.label}
            </button>
          ))}
        </div>

        {/* Plans Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
          {filteredPlans.map((plan) => {
            const key = `hosting:${plan.id}`;
            const isOpen = expanded === plan.id;

            return (
              <motion.div
                key={plan.id}
                layout
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
                className="bg-white border rounded-2xl p-6 flex flex-col hover:shadow-xl transition-shadow"
              >
                <div className="text-xl font-semibold text-gray-900">{plan.name}</div>
                <div className="mt-1 text-gray-600">{plan.description}</div>
                <div className="mt-4 text-3xl font-bold text-blue-600">
                  Rs {plan.price}/mo
                </div>

                {/* Features */}
                {!isMobile ? (
                  // Desktop: Always show features
                  <ul className="mt-4 space-y-2 text-sm text-gray-700 flex-1">
                    {plan.features?.map((f, i) => (
                      <li key={i}>• {f}</li>
                    ))}
                  </ul>
                ) : (
                  // Mobile: Collapsible features
                  <div className="mt-4 flex-1">
                    <button
                      onClick={() =>
                        setExpanded(isOpen ? null : plan.id)
                      }
                      className="flex items-center justify-between w-full text-sm font-medium text-blue-600"
                    >
                      {isOpen ? "Hide Features" : "View Features"}
                      {isOpen ? (
                        <ChevronUp className="w-4 h-4" />
                      ) : (
                        <ChevronDown className="w-4 h-4" />
                      )}
                    </button>

                    <AnimatePresence initial={false}>
                      {isOpen && (
                        <motion.ul
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.3 }}
                          className="mt-3 space-y-2 text-sm text-gray-700 overflow-hidden"
                        >
                          {plan.features?.map((f, i) => (
                            <li key={i}>• {f}</li>
                          ))}
                        </motion.ul>
                      )}
                    </AnimatePresence>
                  </div>
                )}

                <button
                  className="mt-6 w-full bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 disabled:opacity-70 disabled:cursor-not-allowed transition-all"
                  onClick={() => {
                    setAddingKey(key);
                    addItem(key, {
                      title: `${plan.name} Hosting`,
                      subtitle: plan.category,
                      price: plan.price,
                    });
                    setTimeout(() => {
                      setAddingKey(null);
                      setAddedKey(key);
                      setToast({ message: `${plan.name} added to cart` });
                      setTimeout(() => setAddedKey(null), 1200);
                      setTimeout(() => setToast(null), 1800);
                    }, 250);
                  }}
                  disabled={addingKey === key}
                >
                  {addingKey === key
                    ? "Adding..."
                    : addedKey === key
                      ? "Added"
                      : "Add to cart"}
                </button>
              </motion.div>
            );
          })}
        </div>

        {/* Toast (Top Center) */}
        <AnimatePresence>
          {toast && (
            <motion.div
              initial={{ opacity: 0, y: -30 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -30 }}
              transition={{ duration: 0.3 }}
              className="fixed top-6 left-1/2 -translate-x-1/2 z-50 bg-blue-600 text-white px-6 py-3 rounded-full shadow-lg text-sm font-medium"
            >
              {toast.message}
            </motion.div>
          )}
        </AnimatePresence>
      </section>
    </div>
  );
}
